import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Rating from "react-rating";
import AppContext from 'context/Context';
import { getGrays, themeColors } from 'helpers/utils';
import dayjs from "dayjs";
import DrilldownModal from "./DrilldownModal";
import {
    countKeywords,
    knowledgeOf,
    pct,
    questionTokens,
    rankBy,
    ratingOf,
    RATING_LABELS,
    RATING_ORDER,
    RATING_STARS,
    starsOf,
    UNRATED,
    userOf
} from "./conversationStats";

const TOP_N = 7;
// Breakdown lists sit three-across in the modal, so keep each one short.
const DRILL_TOP_N = 5;

// Monday-start week without needing the dayjs isoWeek plugin.
const startOfBucket = (date, unit) => {
    const day = dayjs(date).startOf('day');
    if (unit === 'week') return day.subtract((day.day() + 6) % 7, 'day');
    if (unit === 'month') return day.startOf('month');
    return day;
};

const bucketUnitFor = spanDays => spanDays > 180 ? 'month' : spanDays > 45 ? 'week' : 'day';
const bucketLabel = (date, unit) => dayjs(date).format(unit === 'month' ? 'MMM YY' : 'DD/MM');

// Told in the tooltip rather than as card furniture - it lands the moment
// the pointer is already on the mark the user is about to click.
const DRILL_HINT = '<br/><span style="opacity:.6">Click to break down</span>';

// The category behind a click, whether the bar or its axis label was hit.
const rankedTarget = params => params?.componentType === 'yAxis' ? params.value : params?.name;

const BAR_WIDTH = 14;

// Star level is ordinal - swapping 2 and 5 would change the meaning - so the
// scale is one hue in monotone lightness steps and the reader sees the order in
// the colour itself. Each mode's steps are validated against that mode's card
// surface, so even the palest end stays separated from it. Index 0 is one star.
const STAR_RAMP_LIGHT = ['#90b4e9', '#6f9fe5', '#4d8ae0', '#2973d8', '#1d60b9'];
const STAR_RAMP_DARK = ['#2867bd', '#377ee0', '#5e99ed', '#85b2f2', '#adc9f2'];

// Bars and axis labels carry the label, so a click has to map back to the key.
const RATING_KEY_BY_LABEL = new Map(RATING_ORDER.map(key => [RATING_LABELS[key], key]));

// Ranked magnitude, one series - a single hue, never a colour per bar.
const buildRankedOption = (entries, { colour, colours, axisColour, tooltip, isDark }) => {
    // echarts stacks categories bottom-up, so reverse to put the first entry on top.
    const names = entries.map(([name]) => name).reverse();
    const values = entries.map(([, value]) => value).reverse();
    // One fill per bar when the categories are an ordinal scale, one hue otherwise.
    const fills = colours ? [...colours].reverse() : null;
    // Pinning the max makes the longest bar fill the plot, which is also what
    // lets the hit-target series below cover every row edge to edge.
    const max = Math.max(...values, 1);

    return {
        tooltip: {
            ...tooltip,
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: params => {
                const [p] = params;
                return `${p.name}<br/><strong>${p.value}</strong> question${p.value === 1 ? '' : 's'}${DRILL_HINT}`;
            }
        },
        grid: { left: 0, right: 32, top: 4, bottom: 0, containLabel: true },
        xAxis: { type: 'value', show: false, max },
        yAxis: {
            type: 'category',
            data: names,
            // Long names are truncated, so the label has to be clickable too.
            triggerEvent: true,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: axisColour,
                fontSize: 11,
                formatter: value => value.length > 18 ? `${value.slice(0, 17)}…` : value
            }
        },
        series: [
            {
                type: 'bar',
                // Both series must declare the SAME explicit width. echarts lays
                // series out from a shared starting edge, so unequal widths push the
                // visible bar off-centre; barGap collapses the space between them.
                barWidth: BAR_WIDTH,
                barGap: '-100%',
                // Rounded at the data end, square at the baseline.
                itemStyle: { color: colour, borderRadius: [0, 4, 4, 0] },
                label: {
                    show: true,
                    position: 'right',
                    color: axisColour,
                    fontSize: 11,
                    formatter: '{c}'
                },
                data: fills
                    ? values.map((value, index) => ({ value, itemStyle: { color: fills[index] } }))
                    : values
            },
            {
                // Invisible bar spanning the row, so the full width is a click target
                // - a value of 2 out of 90 draws too few pixels to hit.
                type: 'bar',
                barWidth: BAR_WIDTH,
                z: -1,
                itemStyle: { color: 'transparent' },
                emphasis: { disabled: true },
                tooltip: { show: false },
                data: values.map(() => max)
            }
        ],
        darkMode: isDark
    };
};

const StatTile = ({ label, value, sub, className = '' }) => (
    <Col xs={6} className={`mb-3 ${className}`}>
        <h6 className="text-600 mb-1">{label}</h6>
        <div className="fs-2 fw-semi-bold text-800">{value}</div>
        {sub && <div className="fs--2 text-600 mt-1 text-truncate" title={typeof sub === 'string' ? sub : undefined}>{sub}</div>}
    </Col>
);

StatTile.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node,
    sub: PropTypes.node,
    className: PropTypes.string
};

const LegendKey = ({ colour, label, count, share, onClick }) => (
    <div
        className="d-flex align-items-center fs--1 mb-1"
        style={onClick ? { cursor: 'pointer' } : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={onClick ? event => { if (event.key === 'Enter' || event.key === ' ') onClick(event); } : undefined}
    >
        <span
            className="d-inline-block rounded-circle me-2 flex-shrink-0"
            style={{ width: 8, height: 8, backgroundColor: colour }}
        />
        <span className="text-700 me-auto pe-3">{label}</span>
        <span className="text-900 fw-semi-bold">{count}</span>
        {share !== undefined && <span className="text-600 ms-2" style={{ minWidth: 38, textAlign: 'right' }}>{share}%</span>}
    </div>
);

LegendKey.propTypes = {
    colour: PropTypes.string,
    label: PropTypes.string,
    count: PropTypes.number,
    share: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onClick: PropTypes.func
};

const Insights = ({ data, loading }) => {
    const { config } = useContext(AppContext);
    const isDark = !!config?.isDark;

    const grays = useMemo(() => getGrays(isDark), [isDark]);
    // Matches --falcon-card-bg, so touching marks are separated by surface, not a stroke.
    const surface = isDark ? '#121e2d' : '#ffffff';
    const axisColour = grays[700];
    const gridColour = grays[isDark ? 300 : 200];

    const ratingColours = useMemo(() => {
        const ramp = isDark ? STAR_RAMP_DARK : STAR_RAMP_LIGHT;
        return {
            ...Object.fromEntries(RATING_STARS.map(stars => [String(stars), ramp[stars - 1]])),
            // Unrated sits off the ramp - it is missing data, not a low score.
            [UNRATED]: grays[isDark ? 500 : 400]
        };
    }, [grays, isDark]);

    const rows = useMemo(() => Array.isArray(data) ? data : [], [data]);

    const stats = useMemo(() => {
        const total = rows.length;

        // One counter per bucket, so '1' - '5' and 'unrated' are all counted alike.
        const byRating = Object.fromEntries(RATING_ORDER.map(key => [key, 0]));
        let starSum = 0;
        let noKnowledge = 0;
        let earliest = null;
        let latest = null;

        rows.forEach(row => {
            byRating[ratingOf(row)] += 1;

            const stars = starsOf(row);
            if (stars !== null) starSum += stars;

            if (!row.knowledgeName) noKnowledge += 1;

            const created = row.createdDate ? dayjs(row.createdDate) : null;
            if (created?.isValid()) {
                if (!earliest || created.isBefore(earliest)) earliest = created;
                if (!latest || created.isAfter(latest)) latest = created;
            }
        });

        const unrated = byRating[UNRATED];
        const rated = total - unrated;
        // Mean over rated rows only - an unrated row is absent, not a zero.
        const average = rated > 0 ? starSum / rated : null;

        const users = rankBy(rows, userOf);
        const keywords = countKeywords(rows);

        // Volume over time, bucketed to keep the column count readable.
        const spanDays = earliest && latest ? latest.diff(earliest, 'day') : 0;
        const unit = bucketUnitFor(spanDays);
        const timeline = new Map();
        rows.forEach(row => {
            const created = row.createdDate ? dayjs(row.createdDate) : null;
            if (!created?.isValid()) return;
            const key = startOfBucket(created, unit).valueOf();
            const bucket = timeline.get(key) ?? Object.fromEntries(RATING_ORDER.map(k => [k, 0]));
            bucket[ratingOf(row)] += 1;
            timeline.set(key, bucket);
        });
        const buckets = [...timeline.entries()].sort(([a], [b]) => a - b);

        const busiest = users[0];
        let peak = null;
        buckets.forEach(([key, bucket]) => {
            const volume = RATING_ORDER.reduce((sum, key) => sum + bucket[key], 0);
            if (!peak || volume > peak.volume) peak = { key, volume };
        });

        return {
            total,
            byRating,
            average,
            rated,
            unrated,
            noKnowledge,
            userCount: users.length,
            topUsers: users.slice(0, TOP_N),
            topKeywords: keywords.slice(0, TOP_N),
            leadKeyword: keywords[0],
            busiest,
            peak,
            unit,
            buckets
        };
    }, [rows]);

    const loadingOption = useMemo(() => ({
        color: themeColors.primary,
        maskColor: isDark ? 'rgba(18, 30, 45, 0.6)' : 'rgba(255, 255, 255, 0.6)',
        zlevel: 0
    }), [isDark]);

    const baseTooltip = useMemo(() => ({
        backgroundColor: grays[100],
        borderColor: gridColour,
        borderWidth: 1,
        textStyle: { color: grays[1100], fontSize: 11 },
        extraCssText: 'z-index: 1000'
    }), [grays, gridColour]);

    // One bar per star level in a fixed 5 -> 1 order: the shape a review breakdown
    // is read in, and unlike a single stacked meter it keeps the low levels
    // legible when only a handful of rows land there.
    const ratingOption = useMemo(
        () => buildRankedOption(
            RATING_STARS.map(stars => [RATING_LABELS[stars], stats.byRating[String(stars)]]),
            {
                colours: RATING_STARS.map(stars => ratingColours[String(stars)]),
                axisColour,
                tooltip: baseTooltip,
                isDark
            }
        ),
        [stats.byRating, ratingColours, axisColour, baseTooltip, isDark]
    );

    const usersOption = useMemo(
        () => buildRankedOption(stats.topUsers, {
            colour: themeColors.primary, axisColour, tooltip: baseTooltip, isDark
        }),
        [stats.topUsers, axisColour, baseTooltip, isDark]
    );

    const keywordsOption = useMemo(
        () => buildRankedOption(stats.topKeywords, {
            colour: themeColors.info, axisColour, tooltip: baseTooltip, isDark
        }),
        [stats.topKeywords, axisColour, baseTooltip, isDark]
    );

    const [drilldown, setDrilldown] = useState(null);

    // The dimension drilled into is left out - every row in the subset shares it,
    // so the interesting question is how the subset splits on everything else.
    const breakdownsFor = useCallback((kind, subset) => {
        const dimensions = [];

        if (kind !== 'rating') dimensions.push({
            title: 'Rating split',
            // Fixed 5 -> 1 then unrated, so it reads like the Ratings breakdown.
            entries: RATING_ORDER.map(key => [
                RATING_LABELS[key],
                subset.reduce((count, row) => ratingOf(row) === key ? count + 1 : count, 0)
            ]),
            colours: Object.fromEntries(
                RATING_ORDER.map(key => [RATING_LABELS[key], ratingColours[key]])
            )
        });

        if (kind !== 'user') dimensions.push({
            title: 'Top users',
            entries: rankBy(subset, userOf).slice(0, DRILL_TOP_N),
            colour: themeColors.primary
        });

        dimensions.push({
            title: 'Top knowledge',
            entries: rankBy(subset, knowledgeOf).slice(0, DRILL_TOP_N),
            colour: themeColors.warning
        });

        if (kind !== 'keyword') dimensions.push({
            title: 'Top keywords',
            entries: countKeywords(subset).slice(0, DRILL_TOP_N),
            colour: themeColors.info
        });

        return dimensions;
    }, [ratingColours]);

    const openDrilldown = useCallback((kind, key, heading) => {
        const matches = kind === 'rating'
            ? rows.filter(row => ratingOf(row) === key)
            : kind === 'user'
                ? rows.filter(row => userOf(row) === key)
                // Same tokens the keyword bars were counted from, so the row
                // count always matches the bar the user clicked.
                : rows.filter(row => questionTokens(row.question).has(key));

        if (!matches.length) return;

        setDrilldown({
            heading,
            subject: kind === 'rating' ? RATING_LABELS[key] : key,
            rows: matches,
            total: rows.length,
            share: pct(matches.length, rows.length),
            breakdowns: breakdownsFor(kind, matches)
        });
    }, [rows, breakdownsFor]);

    const closeDrilldown = useCallback(() => setDrilldown(null), []);

    const ratingEvents = useMemo(() => ({
        click: params => {
            const key = RATING_KEY_BY_LABEL.get(rankedTarget(params));
            if (key) openDrilldown('rating', key, 'Ratings');
        }
    }), [openDrilldown]);

    const usersEvents = useMemo(() => ({
        click: params => {
            const user = rankedTarget(params);
            if (user) openDrilldown('user', user, 'Top users');
        }
    }), [openDrilldown]);

    const keywordsEvents = useMemo(() => ({
        click: params => {
            const keyword = rankedTarget(params);
            if (keyword) openDrilldown('keyword', keyword, 'Top keywords');
        }
    }), [openDrilldown]);

    // Kept for the commented-out "Volume over time" card below.
    // eslint-disable-next-line no-unused-vars
    const volumeOption = useMemo(() => {
        const labels = stats.buckets.map(([key]) => bucketLabel(key, stats.unit));
        const series = (name, pick, colour) => ({
            name,
            type: 'bar',
            stack: 'volume',
            barMaxWidth: 24,
            itemStyle: { color: colour, borderColor: surface, borderWidth: 2 },
            data: stats.buckets.map(([, bucket]) => pick(bucket))
        });

        return {
            tooltip: { ...baseTooltip, trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: {
                show: true,
                bottom: 0,
                itemHeight: 8,
                itemGap: 16,
                textStyle: { color: axisColour, fontSize: 11 }
            },
            grid: { left: 0, right: 8, top: 8, bottom: 32, containLabel: true },
            xAxis: {
                type: 'category',
                data: labels,
                axisLine: { lineStyle: { color: gridColour } },
                axisTick: { show: false },
                axisLabel: { color: axisColour, fontSize: 11 }
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                axisLabel: { color: axisColour, fontSize: 11 },
                // Hairline, solid, one step off the surface.
                splitLine: { lineStyle: { color: gridColour, type: 'solid', width: 1 } }
            },
            series: RATING_ORDER.map(key => series(
                RATING_LABELS[key],
                bucket => bucket[key],
                ratingColours[key]
            )),
            darkMode: isDark
        };
    }, [stats, ratingColours, surface, baseTooltip, axisColour, gridColour, isDark]);

    const chartProps = {
        showLoading: loading,
        loadingOption,
        notMerge: true,
        style: { width: '100%' }
    };

    const empty = !loading && stats.total === 0;

    return (
        <>
            <Col xs={12} xl={3}>
                <Card className="p-3 h-100">
                    <h4 className="mb-3">Overview</h4>
                    <Row className="g-0">
                        <StatTile
                            label="Questions"
                            value={stats.total.toLocaleString()}
                            sub={stats.peak ? `Peak: ${bucketLabel(stats.peak.key, stats.unit)} · ${stats.peak.volume}` : '—'}
                        />
                        <StatTile
                            label="Users"
                            value={stats.userCount.toLocaleString()}
                            sub={stats.busiest ? `Top: ${stats.busiest[0]} - ${stats.busiest[1]}` : '—'}
                        />
                        <StatTile
                            label="Rated"
                            value={stats.rated.toLocaleString()}
                            sub={`${pct(stats.rated, stats.total)}% of all questions`}
                        />
                        <StatTile
                            label="No knowledge"
                            value={stats.noKnowledge.toLocaleString()}
                            sub={`${pct(stats.noKnowledge, stats.total)}% unmatched`}
                        />
                    </Row>
                </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
                <Card className="p-3 h-100">
                    <h4 className="mb-1">Ratings</h4>
                    <div className="d-flex align-items-center mb-1">
                        <span className="fs-4 fw-semi-bold text-900 lh-1 me-2">
                            {stats.average === null ? '—' : stats.average.toFixed(1)}
                        </span>
                        {stats.average !== null && (
                            <span className="fs--1 lh-1">
                                <Rating
                                    readonly
                                    fractions={2}
                                    initialRating={stats.average}
                                    fullSymbol={<FontAwesomeIcon icon="star" className="text-primary me-1" />}
                                    emptySymbol={<FontAwesomeIcon icon="star" className="text-300 me-1" />}
                                />
                            </span>
                        )}
                    </div>
                    <p className="fs--2 text-600 mb-2">
                        {stats.rated > 0
                            ? `Mean of ${stats.rated} rated · ${pct(stats.rated, stats.total)}% of all`
                            : 'Nothing rated yet'}
                    </p>
                    {empty
                        ? <p className="fs--1 text-600 mb-0">No results.</p>
                        : <>
                            <ReactECharts
                                {...chartProps}
                                option={ratingOption}
                                onEvents={ratingEvents}
                                style={{ width: '100%', height: 150 }}
                            />
                            {/* Unrated is off the star scale, so it sits under the bars rather
                                than as a sixth level - and doubles as its drill-down handle. */}
                            <LegendKey
                                colour={ratingColours[UNRATED]}
                                label={RATING_LABELS[UNRATED]}
                                count={stats.unrated}
                                share={pct(stats.unrated, stats.total)}
                                onClick={() => openDrilldown('rating', UNRATED, 'Ratings')}
                            />
                        </>}
                </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
                <Card className="p-3 h-100">
                    <h4 className="mb-1">Top users</h4>
                    <p className="fs--2 text-600 mb-2">
                        {stats.userCount > TOP_N ? `${TOP_N} of ${stats.userCount} users` : 'Questions asked'}
                    </p>
                    {empty
                        ? <p className="fs--1 text-600 mb-0">No results.</p>
                        : <ReactECharts
                            {...chartProps}
                            option={usersOption}
                            onEvents={usersEvents}
                            style={{ width: '100%', height: 200 }}
                        />}
                </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
                <Card className="p-3 h-100">
                    <h4 className="mb-1">Top keywords</h4>
                    <p className="fs--2 text-600 mb-2">
                        {stats.leadKeyword
                            ? <>Most frequent: <span className="text-900 fw-semi-bold">{stats.leadKeyword[0]}</span> ({stats.leadKeyword[1]})</>
                            : 'Questions mentioning each term'}
                    </p>
                    {empty
                        ? <p className="fs--1 text-600 mb-0">No results.</p>
                        : <ReactECharts
                            {...chartProps}
                            option={keywordsOption}
                            onEvents={keywordsEvents}
                            style={{ width: '100%', height: 200 }}
                        />}
                </Card>
            </Col>

            {/* <Col xs={12}>
                <Card className="p-3">
                    <h4 className="mb-1">Volume over time</h4>
                    <p className="fs--2 text-600 mb-2">
                        Questions per {stats.unit}, split by rating
                    </p>
                    {empty
                        ? <p className="fs--1 text-600 mb-0">No results.</p>
                        : <ReactECharts {...chartProps} option={volumeOption} style={{ width: '100%', height: 260 }} />}
                </Card>
            </Col> */}

            <DrilldownModal drilldown={drilldown} onHide={closeDrilldown} />
        </>
    )
}

Insights.propTypes = {
    data: PropTypes.array,
    loading: PropTypes.bool
};

export default memo(Insights);